// Pure helpers for the curated Model Book's drawing editor
// (controllers/chart_marks_editor_controller.js; docs/features/CURATED_MODEL_BOOK.md
// § Drawing). Everything here turns a chart click into DATA-SPACE points —
// `{date: "YYYY-MM-DD", price: "<decimal string>"}`, price null on volume
// kinds — and never keeps a pixel or a bar index.
//
// The click/crosshair pattern (subscribeClick + subscribeCrosshairMove,
// series.coordinateToPrice(point.y), an anchor then a live preview) follows
// TradingView's plugin-examples rectangle-drawing-tool / trend-line; the
// drawing itself is ChartMarksLayer's (setPreview), not a second renderer.

// How close (px) a click must land to a bar's high or low to snap to it.
export const SNAP_TOLERANCE_PX = 6
// Kinds whose prices snap to the clicked bar's high/low.
export const SNAP_KINDS = Object.freeze([ "base", "level" ])
// ChartMark::PRICE_DECIMALS.
export const PRICE_DECIMALS = 8

const pad = n => String(n).padStart(2, "0")

// A lightweight-charts time (an ISO string, a BusinessDay object, or a UTC
// timestamp in seconds) as "YYYY-MM-DD", or null.
export function timeToIsoDate(time) {
  if (time === null || time === undefined) return null
  if (typeof time === "string") {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(time)
    return match ? `${match[1]}-${match[2]}-${match[3]}` : null
  }
  if (typeof time === "number" && Number.isFinite(time)) {
    return new Date(time * 1000).toISOString().slice(0, 10)
  }
  if (typeof time === "object" && time.year && time.month && time.day) {
    return `${time.year}-${pad(time.month)}-${pad(time.day)}`
  }
  return null
}

// A positive price as a decimal string with at most 8 dp and no trailing
// zeros ("0.48", "12", "0.00001234"), or null.
export function formatPrice(price) {
  const value = Number(price)
  if (!Number.isFinite(value) || value <= 0) return null
  const fixed = value.toFixed(PRICE_DECIMALS)
  const trimmed = fixed.includes(".") ? fixed.replace(/0+$/, "").replace(/\.$/, "") : fixed
  return trimmed === "0" ? null : trimmed
}

// The price to store for a click at pixel `y`: the bar's high or low when
// either is within `tolerance` px of the click, else `price` unchanged.
// `priceToY` is the price series' priceToCoordinate.
export function snapPrice(price, { bar, y, priceToY, tolerance = SNAP_TOLERANCE_PX }) {
  if (!bar || typeof priceToY !== "function" || y === null || y === undefined) return price
  let best = null
  ;[ bar.high, bar.low ].forEach(candidate => {
    if (!(Number(candidate) > 0)) return
    const cy = priceToY(Number(candidate))
    if (cy === null || cy === undefined) return
    const distance = Math.abs(cy - y)
    if (distance <= tolerance && (!best || distance < best.distance)) best = { price: Number(candidate), distance }
  })
  return best ? best.price : price
}

// One point for `kind` from a click: { date, price } in data space, or null
// when the click cannot be placed (off-bar, or a non-positive price).
// `spec` is the kind's entry in data-kinds ({ pane, min_points, max_points }).
export function pointFor(kind, spec, { time, price, bar, y, priceToY }) {
  const date = timeToIsoDate(time)
  if (!date || !spec) return null
  if (spec.pane === "volume") return { date, price: null }

  const snapped = SNAP_KINDS.includes(kind) ? snapPrice(price, { bar, y, priceToY }) : price
  const formatted = formatPrice(snapped)
  return formatted ? { date, price: formatted } : null
}

// Why a second click on the anchor's own bar can't finish a two-date kind.
export const SAME_BAR_REASON = "Click a different bar for the second point"

// Feed one click into a placement. `anchor` is the pending first point (or
// null). Returns { commit: points | null, anchor: point | null, reason? }:
//   - a one-point kind commits on its click;
//   - a two-point kind anchors on the first click and commits on the second;
//   - a kind that needs two distinct dates (base, contraction: min and max
//     points 2) refuses a second click on the anchor's bar — it keeps the
//     anchor and returns `reason` (the library turns a fast second click into
//     a dblclick on the same bar, which would save a zero-width base);
//   - `level` (1–2 points) commits ONE point when the second click lands on
//     the anchor's bar (a full-width level) and a span otherwise, drawn at
//     the anchor's price (ChartMark::KINDS).
export function placementStep(kind, spec, anchor, point) {
  if (!spec || !point) return { commit: null, anchor }
  const max = spec.max_points || 1
  const min = spec.min_points || max

  if (!anchor) {
    return max <= 1 ? { commit: [ point ], anchor: null } : { commit: null, anchor: point }
  }
  if (point.date === anchor.date) {
    if (min <= 1) return { commit: [ anchor ], anchor: null }
    return { commit: null, anchor, reason: SAME_BAR_REASON }
  }
  if (kind === "level") return { commit: [ anchor, { date: point.date, price: anchor.price } ], anchor: null }
  return { commit: [ anchor, point ], anchor: null }
}

// The transient mark ChartMarksLayer#setPreview draws while placing: the
// anchor (if any) plus the hovered point.
export function previewMark(kind, spec, anchor, point) {
  if (!point || !spec) return null
  let points = [ point ]
  if (anchor) points = kind === "level" ? [ anchor, { date: point.date, price: anchor.price } ] : [ anchor, point ]
  return { id: "preview", kind, points, label: "", note: "", author: null, own: true }
}
